import React, { useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { isEmpty } from '../../../../helpers/common';
import { AVATAR_BORDER } from '../../../../global/constants';
import Avatar from '../../../avatar';
import RenderHtml from '../../../renderHtml';
import DateSeparator from '../dateSeparator';
import ReplyQuote from '../replyQuote';
import MessageActions from '../messageActions';
import MessageReactions from '../messageReactions';
import FileAttachments from '../fileAttachments';
import {
  AvatarWrap,
  Comments,
  CommentSection,
  Desc,
  LeftComment,
  OverLay,
  RightComment,
  TagDesc,
  Time
} from './styles';
import { MessageDetailsInterface } from '../../../../interfaces/chatMessageInterface';
import { CommentFilesInterface } from '../../../../interfaces/TaskInterface';
import { UserModel } from '../../../../mobx/models/user';
import {
  useMessageActionsContext,
  useMessageSelectionContext,
  useMessageEnvironmentContext
} from '../MessageFeedContext';

interface MessageBubbleProps {
  item: MessageDetailsInterface;
  index: number;
  isFirstUserMessage: boolean;
  isShowTime: boolean;
  isShowUserImage: boolean;
  formatedDate: string;
  flag: boolean;
  imageFiles: Array<{ href: string; name: string }>;
  videoFiles: Array<{ href: string; name: string }>;
  audioFiles: Array<{ href: string; name: string }>;
  otherFiles: Array<{ href: string; name: string; maxWidth?: number }>;
  reactionCounts?: Record<string, number>;
}

const MessageBubble = (props: MessageBubbleProps) => {
  const {
    item,
    index,
    isFirstUserMessage,
    isShowTime,
    isShowUserImage,
    formatedDate,
    flag,
    imageFiles,
    videoFiles,
    audioFiles,
    otherFiles,
    reactionCounts
  } = props;

  // Stable callbacks — only re-renders MessageBubble when callbacks change
  const {
    onClickUserPopup,
    onReactOnMessage,
    onClickHandleRichtextClick,
    onClickHandleRefreshFigma,
    onClickFile,
    getReplyMessage
  } = useMessageActionsContext();

  // Volatile selection/UI state — only re-renders when selection state changes
  const { selectedMesaage, openEmoji, deletedMessageId, emojiLoading, figmaRefreshLoading } =
    useMessageSelectionContext();

  // Stable environment info — only re-renders when workspace/user/layout changes
  const { workspace, isProjectMessageDetail } = useMessageEnvironmentContext();

  const localReactionCounts = useMemo(() => {
    const reactionCountMap: Record<string, number> = {};
    (item?.reactions || []).forEach((reaction) => {
      const reactionKey = reaction.ReactionType;
      reactionCountMap[reactionKey] = (reactionCountMap[reactionKey] || 0) + 1;
    });
    return reactionCountMap;
  }, [item?.reactions]);

  const handleUserAvatarClick = useCallback(() => {
    if (!item?.sender) return;
    onClickUserPopup(item.sender as UserModel);
  }, [item?.sender, onClickUserPopup]);

  const handleRichTextClick = useCallback(
    (event: React.MouseEvent) => {
      onClickHandleRichtextClick(event, item);
    },
    [item, onClickHandleRichtextClick]
  );

  const handleRefreshFigma = useCallback(
    (figmaURL: string) => {
      onClickHandleRefreshFigma(item, figmaURL);
    },
    [item, onClickHandleRefreshFigma]
  );

  return (
    <CommentSection id={item?.id}>
      {flag && <DateSeparator date={formatedDate} />}
      <Comments
        isFirstUserMessage={isFirstUserMessage || isShowTime}
        isLastUserMessage={isShowUserImage}
        isReaction={!!(item?.reactions && item?.reactions.length > 0)}>
        <LeftComment isProjectMessageDetail={isProjectMessageDetail} hasUserImage={isShowUserImage}>
          {isShowUserImage && (
            <AvatarWrap role='button' onClick={handleUserAvatarClick}>
              <Avatar
                imgSrc={item?.sender?.profile_photo || ''}
                name={item?.sender?.name ? item?.sender?.name : 'U N'}
                size={36}
                borderVariant={AVATAR_BORDER.NONE}
                className='avatar'
              />
              <OverLay />
            </AvatarWrap>
          )}
        </LeftComment>
        <div>
          {isShowTime && <Time>{dayjs(item?.createdOn).format('hh:mm a')}</Time>}
          <RightComment
            isActive={
              (openEmoji && selectedMesaage === item?.id) || (selectedMesaage === item?.id && !deletedMessageId)
            }>
            <MessageActions message={item} index={index} />

            <TagDesc
              className='tiptap'
              isReply={!isEmpty(item?.reply)}
              showGap={!!item?.message && !!item?.uploadedFile && item?.uploadedFile?.length > 0}
              isLastUserMessage={isShowUserImage}
              isFirstUserMessage={isFirstUserMessage || isShowTime}>
              {!isEmpty(item?.reply) && <ReplyQuote reply={item?.reply} getReplyMessage={getReplyMessage} />}
              {!isEmpty(item?.message) && (
                <Desc role='button' className='description' onClick={handleRichTextClick}>
                  <RenderHtml
                    htmlString={item?.message}
                    figmaLoading={figmaRefreshLoading === item?.id}
                    onClickRefreshFigma={handleRefreshFigma}
                  />
                </Desc>
              )}
              <FileAttachments
                imageFiles={imageFiles as CommentFilesInterface[]}
                videoFiles={videoFiles as CommentFilesInterface[]}
                audioFiles={audioFiles as CommentFilesInterface[]}
                otherFiles={otherFiles as CommentFilesInterface[]}
                onClickFile={onClickFile}
              />
            </TagDesc>
            <MessageReactions
              reactions={item?.reactions}
              reactionCounts={reactionCounts || localReactionCounts}
              emojiLoading={emojiLoading}
              workspaceUsers={workspace?.users}
              onReactOnMessage={onReactOnMessage}
              message={item}
            />
          </RightComment>
        </div>
      </Comments>
    </CommentSection>
  );
};

export default React.memo(MessageBubble);
